import React, { useEffect, useMemo, useState } from "react";
import { RefreshCw, Download, Link2, Check, ChevronDown, ChevronUp, Copy, Trash2, ExternalLink, Save } from "lucide-react";
import {
    SPOTLIGHT_SECTIONS,
    SPOTLIGHT_STATUS_LABELS,
    SPOTLIGHT_TYPE_LABELS,
    formatSpotlightAnswer,
    isSpotlightQuestionVisible,
} from "../../data/spotlightQuestions";

const ADMIN_PASSWORD = 'Unicorn2025!';

const STATUS_OPTIONS = ['new', 'scheduled', 'published', 'archived'];

const formatDate = (value) => {
    if (!value) return '—';
    const str = String(value);
    const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(str);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

/** DATE column → yyyy-mm-dd for <input type="date"> */
const toDateInput = (value) => {
    if (!value) return '';
    const m = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : '';
};

const csvEscape = (value) => {
    const str = String(value ?? '');
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const questionsFor = (type) => (SPOTLIGHT_SECTIONS[type] || SPOTLIGHT_SECTIONS.creator).flatMap((s) => s.questions);

const buildPlainText = (sub) => {
    const lines = [
        `${SPOTLIGHT_TYPE_LABELS[sub.spotlight_type] || 'Creator'} Spotlight — ${sub.name}`,
        `IG: ${sub.ig_handle ? '@' + sub.ig_handle : '—'}`,
        `Email: ${sub.email}`,
        `Headshot: ${sub.headshot_url || '—'}`,
    ];
    if (Array.isArray(sub.property_image_urls) && sub.property_image_urls.length) {
        lines.push(`Property photos: ${sub.property_image_urls.join(' , ')}`);
    }
    lines.push('');
    const answers = sub.answers || {};
    for (const q of questionsFor(sub.spotlight_type)) {
        if (!isSpotlightQuestionVisible(q, answers)) continue;
        const a = formatSpotlightAnswer(q, answers[q.id]);
        if (!a) continue;
        lines.push(q.label);
        lines.push(a);
        lines.push('');
    }
    return lines.join('\n').trim();
};

const copyText = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (_) {
        try {
            const ta = document.createElement('textarea');
            ta.value = text;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            return true;
        } catch (__) {
            return false;
        }
    }
};

const SpotlightAdmin = () => {
    const [submissions, setSubmissions] = useState([]);
    const [counts, setCounts] = useState({ new: 0, scheduled: 0, published: 0, archived: 0, creators: 0, hosts: 0, total: 0 });
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    const [typeFilter, setTypeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [search, setSearch] = useState('');

    const [expandedId, setExpandedId] = useState(null);
    const [edit, setEdit] = useState({}); // { [id]: { status, spotlightDate, emailUrl, adminNote } }
    const [savingId, setSavingId] = useState(null);
    const [savedId, setSavedId] = useState(null);
    const [copiedKey, setCopiedKey] = useState('');
    const [actionError, setActionError] = useState('');

    const fetchSubmissions = async () => {
        setLoading(true);
        setLoadError('');
        try {
            const res = await fetch('/api/spotlight/admin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ adminPassword: ADMIN_PASSWORD, action: 'list', limit: 500 }),
            });
            const data = await res.json();
            if (data.ok) {
                setSubmissions(data.submissions || []);
                setCounts(data.counts || counts);
            } else {
                setLoadError('Failed to load spotlight questionnaires. Please try again.');
            }
        } catch (err) {
            console.error('Error fetching spotlight submissions:', err);
            setLoadError('Failed to load spotlight questionnaires. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSubmissions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return submissions.filter((s) => {
            if (typeFilter !== 'all' && s.spotlight_type !== typeFilter) return false;
            if (statusFilter !== 'all' && s.status !== statusFilter) return false;
            if (q) {
                const hay = `${s.name || ''} ${s.ig_handle || ''} ${s.email || ''}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [submissions, typeFilter, statusFilter, search]);

    const flash = (key) => {
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(''), 1800);
    };

    const copyInviteLink = async (type) => {
        const origin = typeof window !== 'undefined' ? window.location.origin : 'https://postaway.com';
        const path = type === 'host' ? '/hosts/get-featured' : '/creators/get-featured';
        if (await copyText(`${origin}${path}`)) flash(`invite-${type}`);
    };

    const toggleExpand = (sub) => {
        if (expandedId === sub.id) {
            setExpandedId(null);
            return;
        }
        setExpandedId(sub.id);
        setActionError('');
        setEdit((prev) => ({
            ...prev,
            [sub.id]: prev[sub.id] || {
                status: sub.status,
                spotlightDate: toDateInput(sub.spotlight_date),
                emailUrl: sub.email_url || '',
                adminNote: sub.admin_note || '',
            },
        }));
    };

    const updateEdit = (id, field, value) => {
        setEdit((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
    };

    const saveSubmission = async (sub) => {
        const e = edit[sub.id];
        if (!e) return;
        if (e.emailUrl && !/^https?:\/\//i.test(e.emailUrl.trim())) {
            setActionError('Newsletter link must start with http:// or https://');
            return;
        }
        setSavingId(sub.id);
        setActionError('');
        try {
            const res = await fetch('/api/spotlight/admin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    adminPassword: ADMIN_PASSWORD,
                    action: 'update',
                    submissionId: sub.id,
                    status: e.status,
                    spotlightDate: e.spotlightDate || '',
                    emailUrl: e.emailUrl.trim(),
                    adminNote: e.adminNote,
                }),
            });
            const data = await res.json();
            if (data.ok) {
                setSubmissions((prev) => prev.map((s) => (s.id === sub.id ? {
                    ...s,
                    status: data.submission.status,
                    spotlight_date: data.submission.spotlight_date,
                    email_url: data.submission.email_url,
                    admin_note: data.submission.admin_note,
                    published_at: data.submission.published_at,
                } : s)));
                setSavedId(sub.id);
                setTimeout(() => setSavedId(null), 1800);
                // refresh counts quietly
                fetchSubmissions();
            } else {
                setActionError(data.error === 'invalid_email_url' ? 'Newsletter link is not a valid URL.' : 'Failed to save changes.');
            }
        } catch (err) {
            console.error('Error saving spotlight submission:', err);
            setActionError('Failed to save changes.');
        } finally {
            setSavingId(null);
        }
    };

    const quickStatus = async (sub, status) => {
        setEdit((prev) => ({ ...prev, [sub.id]: { ...(prev[sub.id] || {
            status: sub.status,
            spotlightDate: toDateInput(sub.spotlight_date),
            emailUrl: sub.email_url || '',
            adminNote: sub.admin_note || '',
        }), status } }));
        try {
            const res = await fetch('/api/spotlight/admin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ adminPassword: ADMIN_PASSWORD, action: 'update', submissionId: sub.id, status }),
            });
            const data = await res.json();
            if (data.ok) fetchSubmissions();
        } catch (err) {
            console.error('Error updating status:', err);
        }
    };

    const deleteSubmission = async (sub) => {
        if (!window.confirm(`Delete ${sub.name}'s ${SPOTLIGHT_TYPE_LABELS[sub.spotlight_type]} Spotlight questionnaire? This cannot be undone.`)) return;
        try {
            const res = await fetch('/api/spotlight/admin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ adminPassword: ADMIN_PASSWORD, action: 'delete', submissionId: sub.id }),
            });
            const data = await res.json();
            if (data.ok) {
                setExpandedId(null);
                fetchSubmissions();
            }
        } catch (err) {
            console.error('Error deleting spotlight submission:', err);
        }
    };

    const handleExportCsv = () => {
        const creatorQs = questionsFor('creator');
        const hostQs = questionsFor('host');
        // union of question ids, keep creator order then extra host ones
        const seen = new Set();
        const allQs = [...creatorQs, ...hostQs].filter((q) => (seen.has(q.id) ? false : (seen.add(q.id), true)));

        const header = ['Type', 'Status', 'Spotlight Date', 'Name', 'IG Handle', 'Email', 'Headshot URL', 'Property Photo URLs', 'Newsletter Link', 'Admin Note', 'Submitted', ...allQs.map((q) => q.label)];
        const rows = filtered.map((s) => {
            const qs = questionsFor(s.spotlight_type);
            const byId = Object.fromEntries(qs.map((q) => [q.id, q]));
            return [
                SPOTLIGHT_TYPE_LABELS[s.spotlight_type] || s.spotlight_type,
                SPOTLIGHT_STATUS_LABELS[s.status] || s.status,
                toDateInput(s.spotlight_date),
                s.name,
                s.ig_handle ? `@${s.ig_handle}` : '',
                s.email,
                s.headshot_url || '',
                (s.property_image_urls || []).join(' '),
                s.email_url || '',
                s.admin_note || '',
                s.created_at ? new Date(s.created_at).toISOString().slice(0, 10) : '',
                ...allQs.map((q) => {
                    const def = byId[q.id] || q;
                    return formatSpotlightAnswer(def, (s.answers || {})[q.id]);
                }),
            ];
        });
        const csv = [header, ...rows].map((row) => row.map(csvEscape).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `spotlight-questionnaires-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const chip = (active) => ({
        padding: '6px 14px',
        borderRadius: '6px',
        border: active ? '2px solid #0f084b' : '1px solid #d1d5db',
        background: active ? '#0f084b' : '#fff',
        color: active ? '#fff' : '#374151',
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
    });

    return (
        <div className="admin-section">
            <div className="sa-header">
                <div>
                    <h2>
                        Spotlight Questionnaires
                        {counts.new > 0 && (
                            <span style={{ fontSize: '14px', background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '10px', marginLeft: '8px', verticalAlign: 'middle' }}>
                                {counts.new} new
                            </span>
                        )}
                    </h2>
                    <p className="sa-subtitle">
                        Creator &amp; Host of the Week submissions. Schedule a week, paste the newsletter link, then publish to the public Spotlight pages.
                    </p>
                </div>
                <div className="sa-header-actions">
                    <button type="button" className="sa-btn sa-btn-secondary" onClick={() => copyInviteLink('creator')} title="Copy the Creator questionnaire link to send to a creator">
                        {copiedKey === 'invite-creator' ? <Check size={14} /> : <Link2 size={14} />}
                        {copiedKey === 'invite-creator' ? 'Copied' : 'Creator link'}
                    </button>
                    <button type="button" className="sa-btn sa-btn-secondary" onClick={() => copyInviteLink('host')} title="Copy the Host questionnaire link to send to a host">
                        {copiedKey === 'invite-host' ? <Check size={14} /> : <Link2 size={14} />}
                        {copiedKey === 'invite-host' ? 'Copied' : 'Host link'}
                    </button>
                    <button type="button" className="sa-btn sa-btn-secondary" onClick={fetchSubmissions} disabled={loading}>
                        <RefreshCw size={14} />
                        Refresh
                    </button>
                    <button type="button" className="sa-btn sa-btn-primary" onClick={handleExportCsv} disabled={loading || filtered.length === 0}>
                        <Download size={14} />
                        Export CSV
                    </button>
                </div>
            </div>

            <div className="sa-stats">
                <div className="sa-stat">
                    <span className="sa-stat-value">{counts.new}</span>
                    <span className="sa-stat-label">New</span>
                </div>
                <div className="sa-stat">
                    <span className="sa-stat-value">{counts.scheduled}</span>
                    <span className="sa-stat-label">Scheduled</span>
                </div>
                <div className="sa-stat">
                    <span className="sa-stat-value">{counts.published}</span>
                    <span className="sa-stat-label">Published</span>
                </div>
                <div className="sa-stat">
                    <span className="sa-stat-value">{counts.creators} / {counts.hosts}</span>
                    <span className="sa-stat-label">Creators / Hosts</span>
                </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                {[
                    { key: 'all', label: 'All types' },
                    { key: 'creator', label: 'Creators' },
                    { key: 'host', label: 'Hosts' },
                ].map((t) => (
                    <button key={t.key} type="button" onClick={() => setTypeFilter(t.key)} style={chip(typeFilter === t.key)}>{t.label}</button>
                ))}
                <span style={{ width: '1px', height: '22px', background: '#e5e7eb', margin: '0 4px' }} />
                {[
                    { key: 'all', label: 'All', count: counts.total },
                    { key: 'new', label: 'New', count: counts.new },
                    { key: 'scheduled', label: 'Scheduled', count: counts.scheduled },
                    { key: 'published', label: 'Published', count: counts.published },
                    { key: 'archived', label: 'Archived', count: counts.archived },
                ].map((t) => (
                    <button key={t.key} type="button" onClick={() => setStatusFilter(t.key)} style={chip(statusFilter === t.key)}>
                        {t.label} ({t.count})
                    </button>
                ))}
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search name, handle, email"
                    style={{ marginLeft: 'auto', padding: '7px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', minWidth: '220px' }}
                />
            </div>

            {loadError && <div className="admin-error">{loadError}</div>}
            {actionError && <div className="admin-error" style={{ marginBottom: '12px' }}>{actionError}</div>}

            {loading ? (
                <p className="sa-empty">Loading questionnaires…</p>
            ) : filtered.length === 0 ? (
                <p className="sa-empty">
                    {submissions.length === 0
                        ? 'No questionnaires yet. Use the “Creator link” / “Host link” buttons above to invite someone.'
                        : 'No questionnaires match these filters.'}
                </p>
            ) : (
                <div className="ca-table-wrap sp-table-wrap">
                    <table className="ca-table sp-table">
                        <thead>
                            <tr>
                                <th>Person</th>
                                <th className="sp-col-secondary">Type</th>
                                <th className="sp-col-secondary">Status</th>
                                <th className="sp-col-secondary">Spotlight Date</th>
                                <th className="sp-col-secondary">Newsletter</th>
                                <th className="sp-toggle-cell"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((sub) => {
                                const isOpen = expandedId === sub.id;
                                const e = edit[sub.id] || {};
                                const answers = sub.answers || {};
                                const sections = SPOTLIGHT_SECTIONS[sub.spotlight_type] || SPOTLIGHT_SECTIONS.creator;
                                return (
                                    <React.Fragment key={sub.id}>
                                        <tr className="ca-row" style={{ cursor: 'pointer' }} onClick={() => toggleExpand(sub)}>
                                            <td className="sa-cell-name">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    {sub.headshot_url ? (
                                                        <img src={sub.headshot_url} alt="" style={{ width: '38px', height: '38px', borderRadius: '50%', objectFit: 'cover', background: '#f3f4f6', flexShrink: 0 }} />
                                                    ) : (
                                                        <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: '#f3f4f6', flexShrink: 0 }} />
                                                    )}
                                                    <div className="sp-person-cell">
                                                        <div>{sub.name}</div>
                                                        <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>{sub.ig_handle ? `@${sub.ig_handle}` : '—'}</div>
                                                        <div style={{ fontSize: '12px', fontWeight: 500 }}>
                                                            <a className="sa-email" href={`mailto:${sub.email}`} onClick={(ev) => ev.stopPropagation()}>{sub.email}</a>
                                                        </div>
                                                        <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 500 }}>Submitted {formatDate(sub.created_at)}</div>
                                                        <div className="sp-mobile-meta">
                                                            <span className={`sa-badge sa-badge-${sub.spotlight_type}`}>{SPOTLIGHT_TYPE_LABELS[sub.spotlight_type] || sub.spotlight_type}</span>
                                                            <span className={`sa-badge sp-status-${sub.status}`}>{SPOTLIGHT_STATUS_LABELS[sub.status] || sub.status}</span>
                                                            {sub.spotlight_date && <span className="sp-mobile-date">Week of {formatDate(sub.spotlight_date)}</span>}
                                                            {sub.email_url && (
                                                                <a href={sub.email_url} target="_blank" rel="noopener noreferrer" onClick={(ev) => ev.stopPropagation()} className="sp-mobile-link">
                                                                    Newsletter <ExternalLink size={11} />
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="sp-col-secondary"><span className={`sa-badge sa-badge-${sub.spotlight_type}`}>{SPOTLIGHT_TYPE_LABELS[sub.spotlight_type] || sub.spotlight_type}</span></td>
                                            <td className="sp-col-secondary"><span className={`sa-badge sp-status-${sub.status}`}>{SPOTLIGHT_STATUS_LABELS[sub.status] || sub.status}</span></td>
                                            <td className="sp-col-secondary" style={{ whiteSpace: 'nowrap' }}>{formatDate(sub.spotlight_date)}</td>
                                            <td className="sp-col-secondary">
                                                {sub.email_url ? (
                                                    <a href={sub.email_url} target="_blank" rel="noopener noreferrer" onClick={(ev) => ev.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#0f084b', fontWeight: 600, fontSize: '13px' }}>
                                                        Open <ExternalLink size={12} />
                                                    </a>
                                                ) : <span style={{ color: '#9ca3af' }}>—</span>}
                                            </td>
                                            <td className="sp-toggle-cell" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#0f084b', fontSize: '13px', fontWeight: 600 }}>
                                                    <span className="sp-toggle-text">{isOpen ? 'Hide' : 'View'}</span>
                                                    {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                                </span>
                                            </td>
                                        </tr>

                                        {isOpen && (
                                            <tr className="sp-detail-row">
                                                <td colSpan={6} style={{ padding: 0 }}>
                                                    <div className="sp-detail">
                                                        <div className="sp-detail-side">
                                                            {sub.headshot_url && (
                                                                <a href={sub.headshot_url} target="_blank" rel="noopener noreferrer" className="sp-headshot">
                                                                    <img src={sub.headshot_url} alt={`${sub.name} headshot`} />
                                                                </a>
                                                            )}
                                                            <div className="sp-side-actions">
                                                                {sub.headshot_url && (
                                                                    <a className="sa-btn sa-btn-secondary" href={sub.headshot_url} download target="_blank" rel="noopener noreferrer">
                                                                        <Download size={14} /> Headshot
                                                                    </a>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    className="sa-btn sa-btn-secondary"
                                                                    onClick={async () => { if (await copyText(buildPlainText(sub))) flash(`answers-${sub.id}`); }}
                                                                >
                                                                    {copiedKey === `answers-${sub.id}` ? <Check size={14} /> : <Copy size={14} />}
                                                                    {copiedKey === `answers-${sub.id}` ? 'Copied' : 'Copy answers'}
                                                                </button>
                                                            </div>
                                                            {Array.isArray(sub.property_image_urls) && sub.property_image_urls.length > 0 && (
                                                                <div className="sp-property-grid">
                                                                    {sub.property_image_urls.map((url, i) => (
                                                                        <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="sp-property-thumb" title={`Property photo ${i + 1}`}>
                                                                            <img src={url} alt={`Property ${i + 1}`} />
                                                                        </a>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="sp-detail-main">
                                                            <div className="sp-edit-grid">
                                                                <label className="sp-field">
                                                                    <span>Status</span>
                                                                    <select value={e.status || sub.status} onChange={(ev) => updateEdit(sub.id, 'status', ev.target.value)}>
                                                                        {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{SPOTLIGHT_STATUS_LABELS[s]}</option>)}
                                                                    </select>
                                                                </label>
                                                                <label className="sp-field">
                                                                    <span>Spotlight date (week featured)</span>
                                                                    <input type="date" value={e.spotlightDate ?? ''} onChange={(ev) => updateEdit(sub.id, 'spotlightDate', ev.target.value)} />
                                                                </label>
                                                                <label className="sp-field sp-field-wide">
                                                                    <span>Newsletter link (where the image / “All about” link points)</span>
                                                                    <input type="url" placeholder="https://…" value={e.emailUrl ?? ''} onChange={(ev) => updateEdit(sub.id, 'emailUrl', ev.target.value)} />
                                                                </label>
                                                                <label className="sp-field sp-field-wide">
                                                                    <span>Internal note</span>
                                                                    <textarea rows={2} value={e.adminNote ?? ''} onChange={(ev) => updateEdit(sub.id, 'adminNote', ev.target.value)} placeholder="Only visible to admins" />
                                                                </label>
                                                            </div>
                                                            <div className="sp-edit-actions">
                                                                <button type="button" className="sa-btn sa-btn-primary" onClick={() => saveSubmission(sub)} disabled={savingId === sub.id}>
                                                                    {savedId === sub.id ? <Check size={14} /> : <Save size={14} />}
                                                                    {savingId === sub.id ? 'Saving…' : savedId === sub.id ? 'Saved' : 'Save changes'}
                                                                </button>
                                                                {sub.status !== 'published' && (
                                                                    <button type="button" className="sa-btn sa-btn-secondary" onClick={() => quickStatus(sub, 'published')}>
                                                                        Publish now
                                                                    </button>
                                                                )}
                                                                {sub.status === 'published' && (
                                                                    <button type="button" className="sa-btn sa-btn-secondary" onClick={() => quickStatus(sub, 'archived')}>
                                                                        Unpublish (archive)
                                                                    </button>
                                                                )}
                                                                <button type="button" className="sa-btn sa-btn-secondary sp-danger" onClick={() => deleteSubmission(sub)}>
                                                                    <Trash2 size={14} /> Delete
                                                                </button>
                                                            </div>

                                                            <div className="sp-qa">
                                                                {sections.map((sec) => {
                                                                    const visible = sec.questions.filter((q) => isSpotlightQuestionVisible(q, answers));
                                                                    if (!visible.length) return null;
                                                                    return (
                                                                        <div key={sec.section} className="sp-qa-section">
                                                                            <h4>{sec.section}</h4>
                                                                            {visible.map((q) => {
                                                                                const a = formatSpotlightAnswer(q, answers[q.id]);
                                                                                return (
                                                                                    <div key={q.id} className="sp-qa-item">
                                                                                        <div className="sp-qa-q">{q.label}</div>
                                                                                        <div className={`sp-qa-a${a ? '' : ' sp-qa-empty'}`}>{a || 'No answer'}</div>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default SpotlightAdmin;
